import {
  Control,
  Controller,
  FieldErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  UserKeys,
  getContactGroups,
  getDomains,
} from '../../../../api/users-api';
import DomainsAutoComplete from '../../../../shared/domain-autocomplete';
import { ContactSyncModel } from '../../../../../types';
import {
  GroupHeader,
  GroupItems,
} from '../../../../shared/autocomplete-groups-ui';
import AvatarChip from '../../../../shared/avatar-chip';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Delete from '@mui/icons-material/Delete';

interface StepTwoProps {
  control: Control<Partial<ContactSyncModel>>;
  getValues: UseFormGetValues<Partial<ContactSyncModel>>;
  setValue: UseFormSetValue<Partial<ContactSyncModel>>;
  errors: FieldErrors<Partial<ContactSyncModel>>;
  watch: UseFormWatch<Partial<ContactSyncModel>>;
  domainAutoCompleteResponse: string;
  setDomainAutoCompleteResponse: Dispatch<SetStateAction<string>>;
  domains: string[];
  isNameUnique: (name: string) => boolean;
}

export default function StepTwo({
  control,
  errors,
  getValues,
  setValue,
  watch,
  domainAutoCompleteResponse,
  setDomainAutoCompleteResponse,
  isNameUnique,
}: StepTwoProps) {
  const sources = watch('sources')!;
  const dropDownValue = getValues('syncType');
  const contactGroup = watch('groupHref');
  const watchSourceUser = watch('sourceUser') || '';

  function addSelection(domain: string) {
    setValue('sources', [...sources, domain]);
    setDomainAutoCompleteResponse('');
  }

  function removeSelection(domain: string) {
    const newList = sources.filter(
      (selectedDomains) => selectedDomains !== domain
    );
    setValue('sources', newList);
  }

  const { data: contactGroups, isLoading } = useQuery({
    queryKey: [UserKeys.ContactSyncGroupLabels, watchSourceUser],
    queryFn: () => getContactGroups(watchSourceUser),
    enabled: watchSourceUser
      ? watchSourceUser.length > 0 &&
        (dropDownValue === 'contact' || dropDownValue === 'bidirectional')
      : false,
  });

  const systemContactGroups = contactGroups?.filter(
    (group) => group.systemGroup
  );

  const userContactGroups = contactGroups?.filter(
    (group) => !group.systemGroup
  );

  const sortedContactGroups = useMemo(() => {
    const sortedUserContactGroups =
      userContactGroups?.sort((a, b) => a.name.localeCompare(b.name)) || [];

    const sortedSystemContactGroups =
      systemContactGroups?.sort((a, b) => a.name.localeCompare(b.name)) || [];

    return [...sortedUserContactGroups, ...sortedSystemContactGroups];
  }, [userContactGroups, systemContactGroups]);

  const selectedContactGroup = useMemo(() => {
    if (!contactGroup || !sortedContactGroups.length) {
      return null;
    }
    return (
      sortedContactGroups.find(
        (group) => group.resourceName === contactGroup
      ) || null
    );
  }, [contactGroup, sortedContactGroups]);

  const {
    data: domains,
    isLoading: domainsLoading,
    isFetching: domainsFetching,
  } = useQuery({
    queryKey: [UserKeys.Domains],
    queryFn: () => getDomains(),
    enabled: dropDownValue === 'directory',
  });

  return (
    <Box
      sx={{
        viewTransitionName: 'steps',
      }}
    >
      {dropDownValue === 'contact' || dropDownValue === 'bidirectional' ? (
        <Stack gap={2} alignItems="center" sx={{ pt: 2 }}>
          {watchSourceUser.length > 0 ? (
            <Box sx={{ pb: 3 }}>
              <AvatarChip
                email={watchSourceUser}
                type="User"
                size="medium"
                onDelete={() => {
                  setValue('sourceUser', '');
                  setValue('groupTitle', '');
                  setValue('groupHref', '');
                }}
              />
            </Box>
          ) : (
            <Controller
              rules={{
                required: 'Source User is required.',
              }}
              name={'sourceUser'}
              control={control}
              render={() => (
                <Box sx={{ width: 400 }}>
                  <SharedAutocomplete
                    sources={['users']}
                    autocompleteValue={watchSourceUser}
                    setAutoCompleteValue={(value) => {
                      setValue('sourceUser', value);
                    }}
                    errors={!!errors.sourceUser}
                    helperText={errors?.sourceUser?.message || ' '}
                  />
                </Box>
              )}
            />
          )}
          <Stack direction="column" gap={2}>
            <Controller
              rules={{
                required: 'Source Contact Label is required.',
              }}
              name="groupTitle"
              control={control}
              render={() => (
                <Autocomplete
                  disabled={watchSourceUser.length === 0 || isLoading}
                  options={sortedContactGroups}
                  autoComplete
                  autoHighlight
                  selectOnFocus
                  noOptionsText="No Labels Found"
                  renderGroup={(params) => (
                    <li key={params.key}>
                      <GroupHeader>{params.group}</GroupHeader>
                      <GroupItems>{params.children}</GroupItems>
                    </li>
                  )}
                  groupBy={(option) =>
                    option.systemGroup ? 'System Labels' : 'User Labels'
                  }
                  getOptionLabel={(option) => option.name}
                  sx={{ width: 400 }}
                  value={selectedContactGroup}
                  onChange={(_event, newValue) => {
                    if (!newValue) {
                      setValue('groupTitle', '');
                      setValue('groupHref', '');
                      setValue('targetGroupName', '');
                      return;
                    }

                    setValue('groupTitle', newValue.name);
                    setValue('groupHref', newValue.resourceName);
                    setValue('targetGroupName', newValue.name);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Source Label"
                      error={!!errors.groupTitle}
                      helperText={errors?.groupTitle?.message || ' '}
                      slotProps={{
                        input: {
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        },
                      }}
                    />
                  )}
                />
              )}
            />

            <Controller
              name="targetGroupName"
              control={control}
              rules={{
                required: 'Target label name is required.',
                validate: (value) =>
                  isNameUnique(value || '') ||
                  "Label name must be unique. It can't match other contact sync target labels.",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Target Label Name"
                  error={!!errors.targetGroupName}
                  helperText={
                    errors?.targetGroupName?.message ||
                    'Name of the label to be created for the contact sync.'
                  }
                  required
                  sx={{ width: 400 }}
                />
              )}
            />
          </Stack>
        </Stack>
      ) : domainsLoading || domainsFetching ? null : (
        <Stack gap={1} alignItems="center" sx={{ pt: 2 }}>
          <Stack flexDirection={'row'} gap={1}>
            <Controller
              rules={{
                required: 'Source is required.',
              }}
              name={'sources'}
              control={control}
              render={() => (
                <Box sx={{ width: 450 }}>
                  <DomainsAutoComplete
                    size="medium"
                    autoCompleteValue={domainAutoCompleteResponse}
                    setAutoCompleteValue={setDomainAutoCompleteResponse}
                    hasError={!!errors.sources}
                    domains={domains || []}
                    helperText={errors?.sources?.message || ' '}
                    onSelection={(value) => {
                      if (!value) return;
                      addSelection(value);
                    }}
                  />
                </Box>
              )}
            />
          </Stack>
          <Box sx={{ height: 100 }}>
            <Paper sx={{ minWidth: 470, overflow: 'hidden', maxHeight: 195 }}>
              <TableContainer sx={{ minWidth: 470, maxHeight: 200 }}>
                <Table
                  size={'small'}
                  stickyHeader
                  aria-label="searched-domains"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Selected Domains</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sources?.map((domain) => (
                      <TableRow key={domain}>
                        <TableCell>
                          <Stack
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography variant="body2">{domain}</Typography>
                            <Tooltip arrow title="Delete">
                              <span>
                                <IconButton
                                  onClick={() => {
                                    removeSelection(domain);
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
